// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  User as UserIcon,
  Building2,
  CreditCard,
  MessageSquare,
  Upload,
  Loader2,
  KeyRound,
  ExternalLink,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMyOrganization } from "@/hooks/useMyOrganization";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const RECRUITLOOK_EVENTS_ORG_ID = "4a900125-64a5-4a26-b5f6-860fd278a186";
const MAX_BYTES = 5 * 1024 * 1024; // 5MB
const ACCEPTED = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];

const AdminSettings: React.FC = () => {
  const { user, profile, refreshProfile } = useAuth();
  const { organization: myOrg } = useMyOrganization();
  const navigate = useNavigate();

  const orgId = myOrg?.id || RECRUITLOOK_EVENTS_ORG_ID;

  /* ---------------- Account & Profile ---------------- */
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      setName(profile.name || "");
      setEmail(profile.email || user?.email || "");
      setPhotoUrl(profile.profile_photo_url || profile.avatar_url || null);
    }
  }, [profile, user]);

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error("Please upload a PNG, JPG, WebP, or SVG image");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image must be 5MB or smaller");
      return;
    }
    try {
      setPhotoUploading(true);
      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const path = `admin-profiles/${user.id}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("player-photos")
        .upload(path, file, { cacheControl: "3600", upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from("player-photos").getPublicUrl(path);
      setPhotoUrl(`${urlData.publicUrl}?t=${Date.now()}`);
      toast.success("Photo uploaded — remember to save");
    } catch (err: any) {
      toast.error(err.message || "Failed to upload photo");
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleSaveAccount = async () => {
    if (!user?.id) return;
    try {
      setSavingAccount(true);
      const { error } = await supabase
        .from("profiles")
        .update({
          name: name.trim(),
          email: email.trim(),
          profile_photo_url: photoUrl ? photoUrl.split("?")[0] : null,
        })
        .eq("id", profile?.id || user.id);
      if (error) throw error;
      await refreshProfile?.();
      toast.success("Profile saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save profile");
    } finally {
      setSavingAccount(false);
    }
  };

  const handleChangePassword = async () => {
    const target = (email || user?.email || "").trim();
    if (!target) {
      toast.error("No email on file for password reset");
      return;
    }
    try {
      setSendingReset(true);
      const { error } = await supabase.auth.resetPasswordForEmail(target, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      toast.success(`Password reset link sent to ${target}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to send password reset");
    } finally {
      setSendingReset(false);
    }
  };

  /* ---------------- Organization & Branding ---------------- */
  const [orgName, setOrgName] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState("#002E62");
  const [accentColor, setAccentColor] = useState("#BC8D09");
  const [logoUploading, setLogoUploading] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  /* ---------------- Payments ---------------- */
  const [currency, setCurrency] = useState("USD");
  const [feeType, setFeeType] = useState("percentage");
  const [feeValue, setFeeValue] = useState("0");
  const [savingPayments, setSavingPayments] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeConnected, setStripeConnected] = useState(false);

  /* ---------------- Communications ---------------- */
  const [senderName, setSenderName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [smsLabel, setSmsLabel] = useState("");
  const [savingComms, setSavingComms] = useState(false);

  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSettingsLoading(true);
      // Org base record (name, logo, stripe status)
      const { data: org } = await supabase
        .from("organizations")
        .select("name, logo_url, stripe_connect_onboarded_at, stripe_connect_charges_enabled")
        .eq("id", orgId)
        .maybeSingle();
      // Org settings record
      const { data: s } = await supabase
        .from("org_settings")
        .select("*")
        .eq("organization_id", orgId)
        .maybeSingle();
      if (cancelled) return;
      if (org) {
        setOrgName(s?.display_name || org.name || "");
        setLogoUrl(org.logo_url || null);
        setStripeConnected(
          !!org.stripe_connect_onboarded_at || !!org.stripe_connect_charges_enabled
        );
      }
      if (s) {
        setOrgName(s.display_name || org?.name || "");
        setPrimaryColor(s.primary_color || "#002E62");
        setAccentColor(s.accent_color || "#BC8D09");
        setCurrency(s.default_currency || "USD");
        setFeeType(s.platform_fee_type || "percentage");
        setFeeValue(String(s.platform_fee_value ?? "0"));
        setSenderName(s.email_sender_name || "");
        setReplyTo(s.reply_to_email || "");
        setSmsLabel(s.sms_sender_label || "");
      }
      setSettingsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const upsertSettings = async (patch: Record<string, any>) => {
    const { error } = await supabase
      .from("org_settings")
      .upsert(
        { organization_id: orgId, ...patch },
        { onConflict: "organization_id" }
      );
    if (error) throw error;
  };

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error("Please upload a PNG, JPG, WebP, or SVG image");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Logo must be 5MB or smaller");
      return;
    }
    try {
      setLogoUploading(true);
      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const path = `${orgId}/logo.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("org-logos")
        .upload(path, file, { cacheControl: "3600", upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from("org-logos").getPublicUrl(path);
      setLogoUrl(`${urlData.publicUrl}?t=${Date.now()}`);
      toast.success("Logo uploaded — remember to save");
    } catch (err: any) {
      toast.error(err.message || "Failed to upload logo");
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleSaveBranding = async () => {
    try {
      setSavingBranding(true);
      const cleanLogo = logoUrl ? logoUrl.split("?")[0] : null;
      const { error: orgErr } = await supabase
        .from("organizations")
        .update({ name: orgName.trim(), logo_url: cleanLogo })
        .eq("id", orgId);
      if (orgErr) throw orgErr;
      await upsertSettings({
        display_name: orgName.trim(),
        primary_color: primaryColor,
        accent_color: accentColor,
      });
      toast.success("Branding saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save branding");
    } finally {
      setSavingBranding(false);
    }
  };

  const handleSavePayments = async () => {
    try {
      setSavingPayments(true);
      await upsertSettings({
        default_currency: currency,
        platform_fee_type: feeType,
        platform_fee_value: Number(feeValue) || 0,
      });
      toast.success("Payment settings saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save payment settings");
    } finally {
      setSavingPayments(false);
    }
  };

  const handleStripe = async () => {
    try {
      setStripeBusy(true);
      const action = stripeConnected ? "create_login_link" : "create_account";
      const { data, error } = await (supabase as any).functions.invoke(
        "org-stripe-connect-onboarding",
        { body: { action, organization_id: orgId } }
      );
      if (error) throw error;
      const url = data?.onboarding_url || data?.url || data?.login_link?.url;
      if (!url) {
        toast.error("Could not start Stripe flow");
        return;
      }
      if (stripeConnected) window.open(url, "_blank");
      else window.location.href = url;
    } catch (err: any) {
      toast.error(err.message || "Stripe flow failed");
    } finally {
      setStripeBusy(false);
    }
  };

  const handleSaveComms = async () => {
    try {
      setSavingComms(true);
      await upsertSettings({
        email_sender_name: senderName.trim() || null,
        reply_to_email: replyTo.trim() || null,
        sms_sender_label: smsLabel.trim() || null,
      });
      toast.success("Communication defaults saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save communication defaults");
    } finally {
      setSavingComms(false);
    }
  };

  const initials = (name || email || "A").slice(0, 2).toUpperCase();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-heading">Admin Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your profile, organization branding, payments, and communications.
        </p>
      </div>

      <Tabs defaultValue="account" className="space-y-6">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="account" className="gap-2">
            <UserIcon className="h-4 w-4" /> Account & Profile
          </TabsTrigger>
          <TabsTrigger value="branding" className="gap-2">
            <Building2 className="h-4 w-4" /> Organization & Branding
          </TabsTrigger>
          <TabsTrigger value="payments" className="gap-2">
            <CreditCard className="h-4 w-4" /> Payments & Stripe
          </TabsTrigger>
          <TabsTrigger value="comms" className="gap-2">
            <MessageSquare className="h-4 w-4" /> Communications
          </TabsTrigger>
        </TabsList>

        {/* ACCOUNT & PROFILE */}
        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Account & Profile</CardTitle>
              <CardDescription>Your personal admin account details.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 max-w-2xl">
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={photoUrl || undefined} alt={name} />
                  <AvatarFallback className="text-lg">{initials}</AvatarFallback>
                </Avatar>
                <div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoSelect}
                  />
                  <Button
                    variant="outline"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={photoUploading}
                  >
                    {photoUploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Upload photo
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">PNG, JPG, WebP up to 5MB</p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="acc-name">Name</Label>
                  <Input id="acc-name" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc-email">Email</Label>
                  <Input
                    id="acc-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button onClick={handleSaveAccount} disabled={savingAccount}>
                  {savingAccount && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save changes
                </Button>
                <Button variant="outline" onClick={handleChangePassword} disabled={sendingReset}>
                  {sendingReset ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <KeyRound className="h-4 w-4 mr-2" />
                  )}
                  Change password
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ORGANIZATION & BRANDING */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle>Organization & Branding</CardTitle>
              <CardDescription>
                White-label values used across the dashboard and public pages.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 max-w-2xl">
              <div className="space-y-2">
                <Label htmlFor="org-name">Display name</Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-md border bg-muted flex items-center justify-center overflow-hidden">
                    {logoUrl ? (
                      <img src={logoUrl} alt="Logo" className="h-full w-full object-contain" />
                    ) : (
                      <Building2 className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoSelect}
                    />
                    <Button
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoUploading}
                    >
                      {logoUploading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload logo
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">PNG, JPG, SVG up to 5MB</p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="primary-color">Primary color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      id="primary-color"
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="h-10 w-12 rounded border cursor-pointer bg-background"
                    />
                    <Input
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accent-color">Accent color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      id="accent-color"
                      type="color"
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="h-10 w-12 rounded border cursor-pointer bg-background"
                    />
                    <Input
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button onClick={handleSaveBranding} disabled={savingBranding}>
                  {savingBranding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save branding
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAYMENTS & STRIPE */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle>Payments & Stripe</CardTitle>
              <CardDescription>
                Connect Stripe and configure payment defaults for your organization.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 max-w-2xl">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="flex items-center gap-3">
                  {stripeConnected ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium">Stripe Connect</p>
                    <Badge variant={stripeConnected ? "outline" : "secondary"} className="mt-1">
                      {stripeConnected ? "Connected" : "Not connected"}
                    </Badge>
                  </div>
                </div>
                <Button variant="outline" onClick={handleStripe} disabled={stripeBusy}>
                  {stripeBusy ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4 mr-2" />
                  )}
                  {stripeConnected ? "Manage Stripe" : "Connect Stripe"}
                </Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Default currency</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD — US Dollar</SelectItem>
                      <SelectItem value="CAD">CAD — Canadian Dollar</SelectItem>
                      <SelectItem value="EUR">EUR — Euro</SelectItem>
                      <SelectItem value="GBP">GBP — British Pound</SelectItem>
                      <SelectItem value="AUD">AUD — Australian Dollar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Platform fee</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={feeValue}
                      onChange={(e) => setFeeValue(e.target.value)}
                    />
                    <Select value={feeType} onValueChange={setFeeType}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percent %</SelectItem>
                        <SelectItem value="fixed">Fixed {currency}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button onClick={handleSavePayments} disabled={savingPayments}>
                  {savingPayments && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save payment settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMMUNICATIONS */}
        <TabsContent value="comms">
          <Card>
            <CardHeader>
              <CardTitle>Communications</CardTitle>
              <CardDescription>
                Default sender details used for outgoing email and SMS.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 max-w-2xl">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="sender-name">Email sender name</Label>
                  <Input
                    id="sender-name"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="RecruitLook Events"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reply-to">Reply-to email</Label>
                  <Input
                    id="reply-to"
                    type="email"
                    value={replyTo}
                    onChange={(e) => setReplyTo(e.target.value)}
                    placeholder="support@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sms-label">SMS sender label</Label>
                  <Input
                    id="sms-label"
                    value={smsLabel}
                    onChange={(e) => setSmsLabel(e.target.value)}
                    placeholder="RLEVENTS"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button onClick={handleSaveComms} disabled={savingComms}>
                  {savingComms && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save defaults
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/admin-dashboard/communication/templates")}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Manage message templates
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
